import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CONTACT_EMAIL, Z_INDEX } from "../constants";
import { Link } from "react-router-dom";
import { useUseCaseNavLinks } from "../context/UseCaseNavContext";

const DEFAULT_WORDMARK = "INDUCE";
const CONTACT_ADDRESS =
  "737, Kheny Plaza, 4th Floor, Binnimangala, CMH Road, Indiranagar, Bengaluru, Karnataka - 560038, India";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

const FOOTER_LINK_CLASS =
  "font-mono text-[10px] tracking-[0.18em] text-white/30 hover:text-primary-container uppercase transition-colors";

const MEDIA_KIT_URL =
  "https://drive.google.com/drive/folders/1wXRr65EQDmPE0CdCFhmrltWiEVPJKYM-?usp=sharing";

type FooterNavLink =
  | { to: string; label: string; href?: never }
  | { href: string; label: string; to?: never };

const STATIC_FOOTER_LINK_GROUPS: { label: string; links: FooterNavLink[] }[] = [
  {
    label: "Product",
    links: [
      { to: "/what-is-induce", label: "What is Induce" },
      { to: "/product/script-editor", label: "Script Editor" },
      { to: "/product/ai-video-continuity", label: "Memory Graph" },
      { to: "/product/shot-control", label: "Shot Control" },
      { to: "/product/models", label: "AI Models" },
      { to: "/pricing", label: "Pricing" },
    ],
  },
  {
    label: "For Teams",
    links: [
      { to: "/for-filmmakers", label: "Filmmakers" },
      { to: "/for-agencies", label: "Agencies" },
      { to: "/for-creators", label: "Creators" },
      { to: "/for-enterprise", label: "Enterprise" },
    ],
  },
  {
    label: "Resources",
    links: [
      { to: "/blog", label: "Blog" },
      { to: "/faq", label: "FAQ" },
      { to: "/showreel", label: "Showreel" },
      { href: MEDIA_KIT_URL, label: "Media Kit" },
    ],
  },
  {
    label: "Legal",
    links: [
      { to: "/privacy-policy", label: "Privacy Policy" },
      { to: "/terms-of-service", label: "Terms of Service" },
    ],
  },
];

export function SiteCopyright() {
  return (
    <p className="font-mono text-[9px] tracking-[0.15em] text-white/20 uppercase text-center">
      © 2026 Induce. Story Infra for AI Filmmaking. All rights preserved.
    </p>
  );
}

const Footer = ({ wordmark = DEFAULT_WORDMARK }: { wordmark?: string }) => {
  const WORDMARK = wordmark;
  // Scale the giant wordmark by length so longer words (e.g. "RHAPSODY")
  // don't overflow the viewport. 22vw is tuned for a 6-letter word.
  const fontVw = Math.min(22, Math.round((22 * 6) / WORDMARK.length));
  const fontClamp = `clamp(5rem, ${fontVw}vw, 20rem)`;
  const reduced = usePrefersReducedMotion();
  const { links: useCaseLinks } = useUseCaseNavLinks();
  const footerLinkGroups: { label: string; links: FooterNavLink[] }[] = [
    STATIC_FOOTER_LINK_GROUPS[0],
    STATIC_FOOTER_LINK_GROUPS[1],
    ...(useCaseLinks.length > 0
      ? [
          {
            label: 'Use Cases',
            links: useCaseLinks.map(
              (link): FooterNavLink => ({ to: link.to, label: link.label }),
            ),
          },
        ]
      : []),
    ...STATIC_FOOTER_LINK_GROUPS.slice(2),
  ];
  const gridColsClass =
    useCaseLinks.length > 0
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
  const lineRef = useRef<HTMLParagraphElement>(null);
  const textSpanRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [typed, setTyped] = useState("");
  const startedRef = useRef(false);
  const doneTyping = typed.length >= WORDMARK.length;

  useLayoutEffect(() => {
    const span = textSpanRef.current;
    const container = containerRef.current;
    if (!span || !container) return;
    const ro = new ResizeObserver(() => {});
    ro.observe(span);
    return () => ro.disconnect();
  }, [typed]);

  useLayoutEffect(() => {
    if (reduced) setTyped(WORDMARK);
  }, [reduced]);

  useEffect(() => {
    if (reduced) return;
    const el = lineRef.current;
    if (!el) return;
    const timers: number[] = [];
    let cancelled = false;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || cancelled || startedRef.current) return;
        startedRef.current = true;
        let i = 0;
        const step = () => {
          if (cancelled) return;
          i += 1;
          setTyped(WORDMARK.slice(0, i));
          if (i < WORDMARK.length) {
            timers.push(window.setTimeout(step, 80 + Math.floor(Math.random() * 60)));
          }
        };
        step();
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" },
    );

    obs.observe(el);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      obs.disconnect();
    };
  }, [reduced]);

  return (
    <footer
      style={{ zIndex: Z_INDEX.footer }}
      className="relative bg-[#0e0e0e] w-full border-t border-white/5 overflow-hidden"
    >
      <div className="container pt-16 pb-10 flex flex-col gap-16">
          <div className={`grid ${gridColsClass} gap-8 lg:gap-10 w-full`}>
            {footerLinkGroups.map((group) => (
              <div key={group.label} className="text-center justify-self-center sm:text-left">
                <p className="font-mono text-[9px] tracking-[0.18em] uppercase text-white/20 mb-3">
                  {group.label}
                </p>
                <nav className="flex flex-col gap-2.5">
                  {group.links.map((link) =>
                    'href' in link ? (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={FOOTER_LINK_CLASS}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link key={link.to} to={link.to} className={FOOTER_LINK_CLASS}>
                        {link.label}
                      </Link>
                    ),
                  )}
                </nav>
              </div>
            ))}
          </div>

          <div
            data-nosnippet
            className="border-t border-white/5 pt-10 flex flex-col items-center gap-6"
          >
            <SiteCopyright />

            <div className="flex items-center justify-center gap-5">
              <a
                href="https://www.instagram.com/induce.creators"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="inline-flex size-4 items-center justify-center text-white/30 hover:text-primary-container transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/getinduce-com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="inline-flex size-4 items-center justify-center text-white/30 hover:text-primary-container transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.45 20.45h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.354V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://x.com/induceai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
                className="inline-flex size-4 items-center justify-center text-white/30 hover:text-primary-container transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z" />
                </svg>
              </a>
            </div>

            <div className="flex flex-col items-center gap-1.5 text-center max-w-md">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-mono text-[9px] tracking-[0.15em] text-white/30 hover:text-primary-container transition-colors"
              >
                {CONTACT_EMAIL}
              </a>
              <p className="font-mono text-[9px] tracking-[0.15em] text-white/20 leading-relaxed">
                {CONTACT_ADDRESS}
              </p>
            </div>
          </div>
        </div>

      <div
        ref={containerRef}
        className="relative overflow-hidden select-none"
        style={{ height: `calc(${fontClamp} * 0.8)` }}
        aria-hidden="true"
      >
        <p
          ref={lineRef}
          className="font-headline font-black leading-none tracking-tighter text-white/5 text-center absolute inset-x-0 top-0"
          style={{ fontSize: fontClamp, position: "relative", zIndex: 1 }}
        >
          <span ref={textSpanRef}>{typed}</span>
          {!doneTyping && (
            <span className="text-primary-container animate-pulse" style={{ opacity: 0.6 }}>
              ▍
            </span>
          )}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
